import { useEffect, useRef, useState } from "react";
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
  useStripe,
  CardNumberElementProps,
  CardExpiryElementProps,
  CardCvcElementProps,
} from "@stripe/react-stripe-js";
import { StripeElementChangeEvent } from "@stripe/stripe-js";

import { useClickOutside, useFormState } from "common/hooks";
import { Optional } from "common/utils/types";
import { FetchError } from "common/fetch/errors";
import { deepCombineErrors } from "common/utils/errors";
import { CardPaymentDeclineError, StripeCardDeclineCode } from "common/types";

import TextInput from "client/common/components/inputs/TextInput";
import { useSetupIntentMutation, useStripeCardDeclineCodesQuery } from "client/payments/hooks";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import ErrorResults from "client/common/components/results/ErrorResults";
import BaseInput from "client/common/components/inputs/BaseInput";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";

import { PaymentMethodCreate } from "../types";

import styleVars from "client/common/styles/export-variables.less?export";
import styles from "../styles/PaymentForm.module.less";

const stripeInputOptions = {
  style: {
    // input styles must be directly provided here
    // otherwise defaults set by Stripe will override our classes
    base: {
      fontSmoothing: "antialiased",
      fontFamily: styleVars.fontFamilyDirect,
      fontWeight: styleVars.fontWeight,
      fontSize: styleVars.fontSize,
      color: styleVars.textPrimaryColor,
    },
  },
  // Disable placeholder since we render a label above the input
  placeholder: "",
};

enum CardElement {
  Number = "NUMBER",
  Expiry = "EXPIRTY",
  Cvc = "CVC",
}

const exhaustiveCheck = (element: never, entity: string) => {
  console.warn(`Missing ${entity} for ${element}`);
};

const getStripeElement = (
  element: CardElement,
  props: CardNumberElementProps | CardExpiryElementProps | CardCvcElementProps,
): React.ReactNode => {
  switch (element) {
    case CardElement.Number:
      return <CardNumberElement {...(props as CardNumberElementProps)} />;
    case CardElement.Expiry:
      return <CardExpiryElement {...(props as CardExpiryElementProps)} />;
    case CardElement.Cvc:
      return <CardCvcElement {...(props as CardCvcElementProps)} />;
    default:
      exhaustiveCheck(element, "Stripe element definition");
      return null;
  }
};

const getLabelForElement = (element: CardElement) => {
  switch (element) {
    case CardElement.Number:
      return "Card Number";
    case CardElement.Expiry:
      return "MM/YY";
    case CardElement.Cvc:
      return "CVC";
    default:
      exhaustiveCheck(element, "label");
      return "";
  }
};

type StripeInputProps = {
  element: CardElement;
  isFocused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (e: StripeElementChangeEvent) => void;
  className: string;
  inputClassName: string;
};

const StripeInput = ({
  element,
  isFocused,
  onFocus,
  onBlur,
  onChange: onChangeProp,
  className,
  inputClassName,
}: StripeInputProps) => {
  const [isEmpty, setIsEmpty] = useState(true);

  const onChange = (e: StripeElementChangeEvent) => {
    setIsEmpty(e.empty);
    onChangeProp(e);
  };

  const props = {
    options: stripeInputOptions,
    onFocus,
    onBlur,
    onChange,
    className: inputClassName,
  };

  return (
    <BaseInput.AnimationContainer
      className={className}
      label={getLabelForElement(element)}
      isActive={isFocused || !isEmpty}
    >
      {/* Eslint doesn't realize that the stripe inputs are inputs */}
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label>{getStripeElement(element, props)} </label>
    </BaseInput.AnimationContainer>
  );
};

type FormValues = {
  name: string;
  zipcode: string;
};

type PaymentFormProps = {
  initialValues?: FormValues;
  onSubmit: (paymentMethodId: string) => void;
  isLoading: boolean;
  error: Optional<FetchError<PaymentMethodCreate>>;
};

type PaymentFormContentProps = PaymentFormProps & {
  clientSecret: string;
  stripeCardDeclineCodes: StripeCardDeclineCode[];
};

const PaymentFormContent = ({
  clientSecret,
  stripeCardDeclineCodes,
  initialValues,
  onSubmit,
  isLoading: isLoadingProp,
  error: errorProp,
}: PaymentFormContentProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const [focusedElement, setFocusedElement] = useState<CardElement | undefined>();
  const [isNumberComplete, setIsNumberComplete] = useState(false);
  const [isExpiryComplete, setIsExpiryComplete] = useState(false);
  const [isCvcComplete, setIsCvcComplete] = useState(false);
  const isComplete = isNumberComplete && isExpiryComplete && isCvcComplete;

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [cardDeclineError, setCardDeclineError] = useState<CardPaymentDeclineError | undefined>(
    undefined,
  );

  const blurRef = useRef<HTMLDivElement>(null);
  useClickOutside(blurRef, () => setFocusedElement(undefined));

  const [values, updateField] = useFormState<FormValues>({
    name: initialValues?.name ?? "",
    zipcode: initialValues?.zipcode ?? "",
  });

  useEffect(() => {
    const focusExpiry = () => {
      setFocusedElement(CardElement.Expiry);
      elements?.getElement(CardExpiryElement)?.focus();
    };
    const focusCvc = () => {
      setFocusedElement(CardElement.Cvc);
      elements?.getElement(CardCvcElement)?.focus();
    };

    if (isNumberComplete && focusedElement === CardElement.Number) {
      if (isExpiryComplete) {
        focusCvc();
      } else {
        focusExpiry();
      }
    }

    if (isExpiryComplete && focusedElement === CardElement.Expiry) {
      focusCvc();
    }
    // Don't run when focusedElement changes so user can still manually focus completed inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, isNumberComplete, isExpiryComplete]);

  const handleStripeBlur = () => {
    window.setTimeout(() => {
      if (!blurRef.current?.contains(document.activeElement)) {
        setFocusedElement(undefined);
      }
    }, 0);
  };

  const onNumberChange = (e: StripeElementChangeEvent) => {
    setCardDeclineError(undefined);
    setIsNumberComplete(e.complete);
    setError(e.error?.message ?? "");
  };

  const onExpiryChange = (e: StripeElementChangeEvent) => {
    setCardDeclineError(undefined);
    setIsExpiryComplete(e.complete);
    setError(e.error?.message ?? "");
  };

  const onCvcChange = (e: StripeElementChangeEvent) => {
    setCardDeclineError(undefined);
    setIsCvcComplete(e.complete);
    setError(e.error?.message ?? "");
  };

  const handleSubmit = async () => {
    // Only card number element is needed, it will have the expiry/cvc data
    const cardNumber = elements?.getElement(CardNumberElement);

    if (!stripe || !elements || !cardNumber) {
      return;
    }

    if (isComplete) {
      setIsProcessing(true);
    }

    const payload = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: {
          name: values.name,
          address: {
            postal_code: values.zipcode,
          },
        },
      },
    });

    setIsProcessing(false);

    const declineCodeInfo = stripeCardDeclineCodes.find(
      (declindeCodeDescriptor: StripeCardDeclineCode) =>
        declindeCodeDescriptor.decline_code === payload.error?.decline_code,
    );

    if (declineCodeInfo) {
      setError("");
      setCardDeclineError({
        card_decline_description: declineCodeInfo.description,
        card_decline_next_steps: declineCodeInfo.next_steps,
      });
    } else if (payload.error) {
      setCardDeclineError(undefined);
      setError(payload.error?.message ?? "An error occurred verifying this card.");
    } else if (payload.setupIntent?.payment_method) {
      const paymentMethod = payload.setupIntent.payment_method;
      onSubmit(typeof paymentMethod === "string" ? paymentMethod : paymentMethod.id);
    }
  };

  const isLoading = isProcessing || isLoadingProp;
  const canSubmit = !!(isComplete && values.name && values.zipcode && !isLoading);

  return (
    <DrawerLayout.Form
      submitText="Add Payment Method"
      canSubmit={canSubmit}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      // There is not a 1:1 mapping from request to fields, so we collapse any reported field errors
      error={
        errorProp && {
          ...errorProp,
          errors: {
            ...errorProp.errors,
            non_field_errors: deepCombineErrors(errorProp.errors),
          },
        }
      }
    >
      <DrawerLayout.Form.Group align="horizontal">
        <TextInput
          value={values.name}
          label="Name"
          onChange={updateField("name")}
          className={styles.wideInput}
        />
        <TextInput
          value={values.zipcode}
          label="Zipcode"
          onChange={updateField("zipcode")}
          className={styles.normalInput}
          maxLength={5}
        />
      </DrawerLayout.Form.Group>
      <DrawerLayout.Form.Group>
        <BaseInput
          value=""
          onChange={() => null}
          useLabelWrapper={false}
          styles={{
            content: styles.cardInputContent,
          }}
          errors={error ? [error] : undefined}
          isFocused={!!focusedElement}
          renderInputDisplay={({ className }) => (
            <div className={styles.cardInput} ref={blurRef}>
              <StripeInput
                element={CardElement.Number}
                isFocused={focusedElement === CardElement.Number}
                onFocus={() => setFocusedElement(CardElement.Number)}
                onBlur={handleStripeBlur}
                onChange={onNumberChange}
                className={styles.wideInput}
                inputClassName={className}
              />
              <StripeInput
                element={CardElement.Expiry}
                isFocused={focusedElement === CardElement.Expiry}
                onFocus={() => setFocusedElement(CardElement.Expiry)}
                onBlur={handleStripeBlur}
                onChange={onExpiryChange}
                className={styles.normalInput}
                inputClassName={className}
              />
              <StripeInput
                element={CardElement.Cvc}
                isFocused={focusedElement === CardElement.Cvc}
                onFocus={() => setFocusedElement(CardElement.Cvc)}
                onBlur={handleStripeBlur}
                onChange={onCvcChange}
                className={styles.normalInput}
                inputClassName={className}
              />
            </div>
          )}
        />
        {cardDeclineError && <CardPaymentDeclineAlert declineError={cardDeclineError} />}
      </DrawerLayout.Form.Group>
    </DrawerLayout.Form>
  );
};

const PaymentForm = (props: PaymentFormProps) => {
  const stripe = useStripe();
  const {
    data: stripeCardDeclineCodes,
    isLoading: isStripeCardDeclineCodesLoading,
    error: stripeCardDeclineCodeFetchError,
  } = useStripeCardDeclineCodesQuery();
  const {
    mutate: genSetupIntent,
    isLoading: isSetupLoading,
    error: setupIntentError,
    data: setupIntent,
  } = useSetupIntentMutation();

  useEffect(() => {
    if (stripe && !setupIntent) {
      genSetupIntent();
    }
  }, [stripe, setupIntent, genSetupIntent]);

  if (!stripe) {
    return (
      <DrawerLayout.Error
        title="Oh no!"
        description="It looks like there was a problem loading payment information. Please try refreshing your browser or check your internet connection to continue."
        actions={[{ label: "Refresh page", onClick: () => window.location.reload() }]}
      />
    );
  }

  if (isSetupLoading || isStripeCardDeclineCodesLoading) {
    return <LoadingContainer />;
  }

  if (
    !setupIntent ||
    setupIntentError ||
    !stripeCardDeclineCodes ||
    stripeCardDeclineCodeFetchError
  ) {
    return <ErrorResults />;
  }

  return (
    <PaymentFormContent
      clientSecret={setupIntent.client_secret}
      stripeCardDeclineCodes={stripeCardDeclineCodes}
      {...props}
    />
  );
};

export default PaymentForm;
